import type { MessageIndex } from "@/scripts/build-message-index"
import {
  extractRenderKey,
  hasRenderKey,
  stripRenderKey,
} from "@/lib/intl/render-key-sentinel"

const DATA_KEY = "data-i18n-key"

/**
 * Read render-key payloads off the live DOM and turn them into `data-i18n-key`
 * stamps, the next-intl analogue of `inject-cms-stamps`. Mirrors that module's
 * contract: client-only (the byte-clean public build never stamps), idempotent,
 * no network.
 *
 * When a signed-in editor activates edit mode, the i18n wrapper re-renders every
 * `t()` string with an invisible payload (see `@/lib/intl/use-translations`).
 * This walks the text nodes, decodes the exact `namespace.key`, stamps it onto
 * the text node's element, then strips the payload so the visible DOM is clean
 * again. The editor then resolves a click to that key exactly — no value→key
 * guess, no "on this page" disambiguation radio.
 *
 * Robustness (option c): a decoded key is only trusted when the catalog actually
 * holds it (`index.byKey`), so a mangled payload falls back to value resolution
 * rather than stamping a bogus key. Returns the number of elements stamped.
 * (AHD-7514)
 */
export function injectI18nStamps(
  root: HTMLElement,
  index: MessageIndex | null
): number {
  const pending: Text[] = []
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.nodeValue && hasRenderKey(node.nodeValue))
      pending.push(node as Text)
  }
  let stamped = 0
  for (const text of pending) {
    const decoded = extractRenderKey(text.nodeValue ?? "")
    if (!decoded) {
      // Mangled payload: scrub the sentinel chars but don't stamp a guess.
      text.nodeValue = stripRenderKey(text.nodeValue ?? "")
      continue
    }
    const { key, clean } = decoded
    const el = text.parentElement
    if (el && (!index || key in index.byKey) && !el.hasAttribute(DATA_KEY)) {
      el.setAttribute(DATA_KEY, key)
      stamped++
    }
    text.nodeValue = clean
  }
  return stamped
}

/**
 * Undo `injectI18nStamps`: drop every `data-i18n-key` and scrub any residual
 * payloads, so deactivating the editor leaves the DOM exactly as the public
 * build shipped it.
 */
export function stripI18nStamps(root: HTMLElement): void {
  root
    .querySelectorAll(`[${DATA_KEY}]`)
    .forEach((el) => el.removeAttribute(DATA_KEY))
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.nodeValue && hasRenderKey(node.nodeValue))
      node.nodeValue = stripRenderKey(node.nodeValue)
  }
}
